import {
    IOrganisationUnaSettings,
    Organisation,
    OrganisationInvoiceData,
    OrganisationSettingsInput,
    OrganisationUnaSettings,
    PaymentTypes
} from '@/api/klip-api.proxy';
import { organisationSettings, settings } from '@/app/settings/settings-routes';
import {useUserStore} from '@/app/shared/state/UserDataModule';
import {EUserRole} from '@/app/shared/state/user-role';
import {computed, defineComponent, onMounted, ref, watch} from 'vue';
import KlOrganisationUnaZones from './components/kl-organisation-una-zones/kl-organisation-una-zones';
import {useRouter} from '@/router/router-vue3';
import {useKlipApiProxy} from '@/plugins/proxy-client';
import {useForm} from 'vee-validate';
import {useMetaStore} from '@/stores/meta-store';
import {RouteRecordRaw} from 'vue-router';
import * as yup from 'yup';
import {toTypedSchema} from '@vee-validate/yup';
import {
    klValidationFields,
    ValidationFieldsType
} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlEditSubOrganisation',
    props: {
        organisationId: { type: String, default: '' },
    },
    setup(props) {

        const schema = yup.object({
            organisationName: yup.string().required().max(450).label('Naam organisatie'),
        });

        const router = useRouter();

        const form = useForm({
            validationSchema: toTypedSchema(schema),
        });
        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema);

        const disableSubmit = computed((): boolean => {
            return !form.meta.value.valid || !form.meta.value.dirty;
        });

        const organisation = ref<Organisation>();
        const organisationNameField = fields.organisationName.getValue<string>();
        watch(() => organisation.value?.name, (newValue: string) => { fields.organisationName.value.value = newValue; },{ flush: 'sync' });

        const organisationUnaZones = ref<InstanceType<typeof KlOrganisationUnaZones>>(null);

        const isSending = ref<boolean>(false);
        const isLoading = ref<boolean>(true);

        const parentOrganisationName = ref<string>('');
        const isUnaConfirmed = ref<boolean>(false);
        const organisationUnaSettings = ref<OrganisationUnaSettings>();

        const zonesDisplayName = ref<string>('');

        const routeSettings = computed((): RouteRecordRaw => {
            return organisationSettings;
        });

        const isEditable = computed((): boolean => {
            return organisation.value.parentId !== null && hasOrganisationManagerRole.value;
        });

        const showUnaSettings = computed((): boolean => {
            if (hasUnaEditRole.value) {
                return true;
            }

            return hasUnaReadRole.value;
        });

        const hasUnaEditRole = computed((): boolean => {
            return useUserStore().hasRole([EUserRole.una_manager, EUserRole.una_reader, EUserRole.klim_admin]);
        });

        const hasUnaReadRole = computed((): boolean => {
            return useUserStore().hasRole([EUserRole.una_operator]) && organisationUnaSettings.value && organisationUnaSettings.value.isUna;
        });

        const hasOrganisationManagerRole = computed((): boolean => {
            return useUserStore().hasRole([EUserRole.org_manager]);
        });

        const unaSettingsUpdated = (newSettings: IOrganisationUnaSettings) => {
            if (props.organisationId === useUserStore().user.klipOrganisationApiId) {
                useUserStore().updateUserProfile(true);
            }
            isUnaConfirmed.value = true;

            organisationUnaSettings.value.displayName = newSettings.displayName;
            organisationUnaSettings.value.subjectSigningCertificate = newSettings.subjectSigningCertificate;
        }

        const _submit = () => {
            isSending.value = true;
            const organisationInput = new OrganisationSettingsInput();
            organisationInput.organisation = new Organisation();
            organisationInput.organisation.name = organisation.value.name;
            organisationInput.organisation.organisationId = organisation.value.organisationId;
            organisationInput.organisation.parentId = organisation.value.parentId;
            useKlipApiProxy().organisation_EditOrganisation(organisationInput).then(() => {
                router.push({ name: routeSettings.value.name, hash: "#organisation" });
            }).finally(() => {
                isSending.value = false;
            })
        }

        onMounted(() => {
            useKlipApiProxy().organisation_LoadEditOrganisationView(props.organisationId).then(response => {
                organisation.value = response.result.organisation;
                organisationUnaSettings.value = response.result.unaSettings;
                useMetaStore().setPageTitle(organisation.value.name);

                if (organisation.value.parentId) {
                    const parentOrganisation = useUserStore().user.subOrganisations?.find(s => s.id === organisation.value.parentId);
                    if (parentOrganisation) {
                        parentOrganisationName.value = parentOrganisation.name;
                    }
                }
                if (organisationUnaSettings.value) {
                    isUnaConfirmed.value = organisationUnaSettings.value.isUna;
                }
            }).finally(() => {
                isLoading.value = false;

                form.resetForm({ values: form.values });
            });

            if (useUserStore().user.subOrganisations.length === 1)
            {
                useMetaStore().setParent(settings);
            }
        });

        const _onInvalidSubmit = (submittedValues) => {
            console.log('_onInvalidSubmit', submittedValues);
        }

        const onSubmit = form.handleSubmit(_submit, _onInvalidSubmit);

        return {
            organisationNameField,

            organisationUnaZones,

            isSending,
            isLoading,

            parentOrganisationName,
            isUnaConfirmed,
            organisation,
            organisationUnaSettings,
            zonesDisplayName,
            routeSettings,
            isEditable,
            showUnaSettings,
            hasUnaEditRole,
            hasUnaReadRole,
            hasOrganisationManagerRole,

            unaSettingsUpdated,

            onSubmit,
            disableSubmit,

            fields,
        }
    }
})
